import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSource, useSources } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import {
  ANALYSIS_PREF_KEYS,
  ANALYSIS_SCHEDULE_OPTIONS,
  CONTINUE_WATCHING_LIMITS,
  CONTINUE_WATCHING_WEEKS,
  DEFAULT_SCAN_INTERVAL,
  getLibraryWatchStatus,
  getPrefs,
  libraryFormFromPrefs,
  libraryPrefsPatch,
  patchPrefs,
  PLAY_COMPLETION_OPTIONS,
  PLAYED_THRESHOLDS,
  sameLibraryForm,
  SCAN_INTERVALS,
  watchStatusText,
  type LibraryForm,
  type ServerPrefs,
} from "../../../lib/libraryAdmin";
import { useAdvancedSettings } from "../../../lib/settingsAdvanced";
import { Checkbox, ErrorNote, errorMessage, Field, Select } from "../../../components/ui";
import {
  AdvancedToggle,
  SheetHeader,
  SheetInset,
  SheetLoading,
  SheetSection,
  UnsavedBar,
  useDraft,
  useLastTouched,
} from "../../../components/settings-sheet";
import { ServerSettingsGuard } from "../../../components/server-settings";

export const Route = createFileRoute("/_authed/settings/server/$serverId/library")({
  component: ServerLibrarySettings,
});

/**
 * Settings › Library — Plex's Settings › Server › Library, the parts Rago
 * has machinery for: automatic (watched-folder) and periodic scanning,
 * the trash sweep, the Continue Watching window and size, the played
 * threshold and the play-completion rule, behind Show advanced the two
 * owner-only switches (media deletion, scanner priority), and Media
 * analysis as its own section below (Plex's four analysis selects; the
 * preview interval is fixed at 10 s). One draft, saved from the floating
 * "Unsaved changes" pill (settings-sheet UnsavedBar, as on Settings › Trakt;
 * Kenny); every control feature-detects on the key the server reports, so
 * an older rago-server shows exactly what it supports.
 */
function ServerLibrarySettings() {
  const { serverId } = Route.useParams();
  // Subscribing to the registry is what makes this work on a cold load:
  // getSource() is a sync snapshot, and without a re-render when servers
  // hydrate the prefs query would stay disabled forever.
  useSources();
  const source = getSource(serverId);
  const isServer = source instanceof ServerSource;
  const qc = useQueryClient();
  const [advanced] = useAdvancedSettings();

  const prefs = useQuery({
    queryKey: ["settings", "prefs", serverId],
    enabled: isServer,
    queryFn: () => getPrefs((source as ServerSource).conn),
  });
  const has = (k: keyof ServerPrefs) => Boolean(prefs.data && k in prefs.data);

  // The watcher's live state — what the server does now, never the draft.
  // Older servers 404 the route; the line simply stays hidden then.
  const watch = useQuery({
    queryKey: ["settings", "library-watch", serverId],
    enabled: isServer && has("watchLibraryFolders"),
    retry: false,
    refetchInterval: 15_000,
    queryFn: () => getLibraryWatchStatus((source as ServerSource).conn),
  });

  // One draft form seeded from the server's values, following them while it
  // is still clean (settings-sheet useDraft).
  const baseline = useMemo(
    () => (prefs.data ? libraryFormFromPrefs(prefs.data) : null),
    [prefs.data],
  );
  const [draft, setDraft] = useDraft(baseline, sameLibraryForm);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const form = draft ?? baseline;
  const patch =
    baseline && form && prefs.data ? libraryPrefsPatch(baseline, form, prefs.data) : {};
  const dirty = Object.keys(patch).length > 0;
  // The control changed last: it gets the focus back when the pill leaves.
  const touched = useLastTouched();

  function update(p: Partial<LibraryForm>) {
    setDraft((f) => ({ ...(f as LibraryForm), ...p }));
    setSaved(false);
    setError(null);
  }

  function revert() {
    setDraft(baseline);
    setError(null);
  }

  async function save() {
    if (!isServer || !form || !dirty || saving) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await patchPrefs(source.conn, patch);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["settings", "prefs", serverId] }),
        qc.invalidateQueries({ queryKey: ["settings", "library-watch", serverId] }),
        // The analysis selects gate the generate-* tasks' enabled flags.
        qc.invalidateQueries({ queryKey: ["settings", "tasks", serverId] }),
      ]);
      setSaved(true);
    } catch (e) {
      setError(e);
    } finally {
      setSaving(false);
    }
  }

  const watchLine = watchStatusText(watch.data);
  const hasAnalysis = ANALYSIS_PREF_KEYS.some(has);
  const showAdvanced = advanced && (has("allowMediaDeletion") || has("scannerLowPriority"));

  return (
    <ServerSettingsGuard serverId={serverId} heading="Library">
      {() => (
        // .rago-sheet-fill: the root fills the sheet, so the pill docks at its foot.
        <div className="rago-sheet-fill">
          <SheetHeader title="Library" action={<AdvancedToggle />} />

          {prefs.isLoading || !form ? (
            // A load that failed says so below instead of spinning for good.
            prefs.error ? null : (
              <SheetLoading />
            )
          ) : (
            // Every control's change bubbles to here: the one changed last.
            <div onChange={touched.onChange}>
              <SheetSection title="Scanning">
                {has("watchLibraryFolders") ? (
                  <div className="mb-4">
                    <Checkbox
                      id="scan-auto"
                      checked={form.watchFolders}
                      onChange={(on) => update({ watchFolders: on })}
                      label="Scan libraries automatically"
                      hint={
                        <>
                          Watches every library folder and runs a partial scan of what
                          changed, about 30 seconds after files stop changing. Local disks
                          work; network mounts (NFS, SMB) usually can't report changes — keep
                          periodic scanning on for those.
                          {watchLine ? (
                            <span className="mt-1 block text-[var(--rago-text-2)]">
                              {watchLine}
                            </span>
                          ) : null}
                        </>
                      }
                    />
                  </div>
                ) : null}
                <div className="mb-4">
                  <Checkbox
                    id="scan-periodic"
                    checked={form.scanInterval > 0}
                    onChange={(on) => update({ scanInterval: on ? DEFAULT_SCAN_INTERVAL : 0 })}
                    label="Scan my libraries periodically"
                    hint="Rago re-walks every library's folders on this schedule and picks up anything new."
                  />
                </div>
                <Field label="Scan interval" htmlFor="scan-interval">
                  <Select
                    id="scan-interval"
                    value={String(form.scanInterval > 0 ? form.scanInterval : DEFAULT_SCAN_INTERVAL)}
                    disabled={form.scanInterval === 0}
                    onChange={(v) => update({ scanInterval: Number(v) })}
                  >
                    {SCAN_INTERVALS.map(([minutes, label]) => (
                      <option key={minutes} value={minutes}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>
                {has("emptyTrashAfterScan") ? (
                  <Checkbox
                    id="empty-trash"
                    checked={form.emptyTrash}
                    onChange={(on) => update({ emptyTrash: on })}
                    label="Empty trash automatically after every scan"
                    hint="Off until you turn it on: a share that is not mounted looks like an empty folder, and the sweep would take your watch history with the items. Removes items whose files are gone; skipped when a library folder was unreachable. Each library's menu under Libraries can empty its trash by hand."
                  />
                ) : null}
              </SheetSection>

              {has("continueWatchingWeeks") ? (
                <SheetSection title="Continue Watching">
                  <Field
                    label="Keep items for"
                    htmlFor="cw-weeks"
                    hint="A show or movie you last watched longer ago than this leaves Continue Watching — it stays resumable from its own page."
                  >
                    <Select
                      id="cw-weeks"
                      value={String(form.continueWatchingWeeks)}
                      onChange={(v) => update({ continueWatchingWeeks: Number(v) })}
                    >
                      {CONTINUE_WATCHING_WEEKS.map(([weeks, label]) => (
                        <option key={weeks} value={weeks}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field
                    label="Maximum items"
                    htmlFor="cw-limit"
                    hint="How many rows the Continue Watching hub shows on Home."
                  >
                    <Select
                      id="cw-limit"
                      value={String(form.continueWatchingLimit)}
                      onChange={(v) => update({ continueWatchingLimit: Number(v) })}
                    >
                      {CONTINUE_WATCHING_LIMITS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </SheetSection>
              ) : null}

              {has("videoPlayedThreshold") ? (
                <SheetSection title="Playback">
                  <Field
                    label="Video played threshold"
                    htmlFor="played-threshold"
                    hint="How much of a video counts as watched."
                  >
                    <Select
                      id="played-threshold"
                      value={String(form.playedThreshold)}
                      onChange={(v) => update({ playedThreshold: Number(v) })}
                    >
                      {PLAYED_THRESHOLDS.map((pct) => (
                        <option key={pct} value={pct}>
                          {pct}%
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {has("videoPlayCompletionBehavior") ? (
                    <Field
                      label="Video play completion behaviour"
                      htmlFor="play-completion"
                      hint="When a video counts as watched: at the played threshold above, or once playback reaches a credits marker (Media analysis below). Without a marker every choice is the threshold."
                    >
                      <Select
                        id="play-completion"
                        value={form.playCompletion}
                        onChange={(v) => update({ playCompletion: v as LibraryForm["playCompletion"] })}
                      >
                        {PLAY_COMPLETION_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : null}
                </SheetSection>
              ) : null}

              {showAdvanced ? (
                <SheetSection title="Advanced">
                  {has("allowMediaDeletion") ? (
                    <div className={has("scannerLowPriority") ? "mb-4" : ""}>
                      <Checkbox
                        id="allow-deletion"
                        checked={form.allowMediaDeletion}
                        onChange={(on) => update({ allowMediaDeletion: on })}
                        label="Allow media deletion"
                        hint="The owner of this server can delete media files from disk from an item's page. Deleted files are gone for good; the item moves to the library's trash."
                      />
                    </div>
                  ) : null}
                  {has("scannerLowPriority") ? (
                    <div>
                      <Checkbox
                        id="scanner-low-priority"
                        checked={form.scannerLowPriority}
                        onChange={(on) => update({ scannerLowPriority: on })}
                        label="Run the scanner at low priority"
                        hint="Scans one file at a time and runs the analysis processes at low CPU priority, so playback stays smooth on a small box. Scans take longer."
                      />
                    </div>
                  ) : null}
                </SheetSection>
              ) : null}

              {hasAnalysis ? (
                <SheetSection title="Media analysis">
                  {/* Plex files these under Library › Advanced; Kenny wanted
                      them as their own category (2026-09-13). Each select
                      gates a scheduled task; "…and when media is added" also
                      runs it for new files right after their scan. */}
                  {has("generatePreviewThumbnails") ? (
                    <Field
                      label="Generate video preview thumbnails"
                      htmlFor="analysis-previews"
                      hint="Frames the player shows while scrubbing (also under the seek bar's hover). Thumbnail generation may take a long time, cause high CPU usage, and consume additional disk space — it runs one file at a time and pauses while someone is streaming."
                    >
                      <Select
                        id="analysis-previews"
                        value={form.previewThumbnails}
                        onChange={(v) => update({ previewThumbnails: v as LibraryForm["previewThumbnails"] })}
                      >
                        {ANALYSIS_SCHEDULE_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : null}
                  {has("generateIntroMarkers") ? (
                    <Field
                      label="Generate intro video markers"
                      htmlFor="analysis-intro"
                      hint="Finds the intro a season's episodes share by comparing their audio, exposing the Skip Intro button in the player. TV only; a season needs at least two episodes."
                    >
                      <Select
                        id="analysis-intro"
                        value={form.introMarkers}
                        onChange={(v) => update({ introMarkers: v as LibraryForm["introMarkers"] })}
                      >
                        {ANALYSIS_SCHEDULE_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : null}
                  {has("generateCreditsMarkers") ? (
                    <Field
                      label="Generate credits video markers"
                      htmlFor="analysis-credits"
                      hint="Detects movie and episode end credits, exposing Skip Credits and bringing Up Next forward to the credits."
                    >
                      <Select
                        id="analysis-credits"
                        value={form.creditsMarkers}
                        onChange={(v) => update({ creditsMarkers: v as LibraryForm["creditsMarkers"] })}
                      >
                        {ANALYSIS_SCHEDULE_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : null}
                  {has("generateChapterThumbnails") ? (
                    <Field
                      label="Generate chapter thumbnails"
                      htmlFor="analysis-chapters"
                      hint="One still per chapter for the Chapters strip on an item's page and the player's chapter menu. Only files that carry chapters; cheap."
                    >
                      <Select
                        id="analysis-chapters"
                        value={form.chapterThumbnails}
                        onChange={(v) => update({ chapterThumbnails: v as LibraryForm["chapterThumbnails"] })}
                      >
                        {ANALYSIS_SCHEDULE_OPTIONS.map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : null}
                  {hasAnalysis ? (
                    <p className="text-xs text-[var(--rago-text-3)]">
                      Scheduled runs happen in the maintenance window — see{" "}
                      <Link
                        to="/settings/server/$serverId/tasks"
                        params={{ serverId }}
                        className="text-[var(--rago-text-2)] underline decoration-[var(--rago-line-2)] underline-offset-2 hover:text-[var(--rago-text-1)]"
                      >
                        Scheduled Tasks
                      </Link>
                      . An item's ⋯ menu can run the enabled analyses on one title right away.
                    </p>
                  ) : null}
                </SheetSection>
              ) : null}
            </div>
          )}

          {prefs.error ? (
            <SheetInset>
              <ErrorNote error={prefs.error} />
            </SheetInset>
          ) : null}

          {/* The root's direct child (its dock rides at the sheet's foot), and
              always rendered, as UnsavedBar asks. */}
          <UnsavedBar
            dirty={dirty}
            saving={saving}
            saved={saved}
            error={error ? errorMessage(error) : null}
            onSave={() => void save()}
            onRevert={revert}
            onFocusLost={touched.focus}
          />
        </div>
      )}
    </ServerSettingsGuard>
  );
}
